import React, { memo, useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { validators, formats } from "investira.sdk";
import { ATIVO_TIPO } from "@investirapri/financa.js/lib/enums";
import {
  Typography,
  Stack,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TabContext,
  Box,
  TabList,
  Tab,
  TableContainer,
  Badge,
} from "investira.react.components";
import { PessoaImage } from "../../../../../components/molecules";
import { PATHS } from "../../../../../enums";

const ConcentracaoDetalhesAtivos = memo((props) => {
  const { regra } = props;

  const [value, setValue] = useState(null);
  const [groupByAtivoTipo, setGroupByAtivoTipo] = useState([]);

  const commonColumns = {
    descricao: { key: "descricao", label: "", align: "left" },
    pessoa: { key: "pessoa", label: "", align: "left" },
    ativo: { key: "ativo", label: "Ativo", align: "left" },
    ticker: { key: "ticker", label: "Ticker", align: "left" },
    isin: { key: "isin", label: "ISIN", align: "left" },
    emissor: { key: "emissor", label: "Emissor", align: "left" },
    financeiro: { key: "financeiro", label: "Financeiro", align: "right" },
    vencimento: { key: "vencimento", label: "Vencimento", align: "left" },
    codigo: { key: "codigo", label: "Código", align: "left" },
    ativo_tipo: { key: "ativo_tipo", label: "Tipo de Ativo", align: "left" },
    serie: { key: "serie", label: "Série", align: "left" },
    curva: { key: "curva_id", label: "Curva", align: "left" },
  };

  const columns = {
    FND: [
      commonColumns.pessoa,
      commonColumns.ativo,
      commonColumns.isin,
      commonColumns.codigo,
      commonColumns.financeiro,
    ],
    TPU: [
      commonColumns.pessoa,
      commonColumns.ativo,
      commonColumns.vencimento,
      commonColumns.isin,
      commonColumns.curva,
      commonColumns.serie,
      commonColumns.codigo,
      commonColumns.financeiro,
    ],
    TPR: [
      commonColumns.pessoa,
      commonColumns.ativo,
      commonColumns.ticker,
      commonColumns.isin,
      commonColumns.emissor,
      commonColumns.curva,
      commonColumns.vencimento,
      commonColumns.codigo,
      commonColumns.financeiro,
    ],
    TBA: [
      commonColumns.pessoa,
      commonColumns.ativo,
      commonColumns.ticker,
      commonColumns.isin,
      commonColumns.emissor,
      commonColumns.curva,
      commonColumns.vencimento,
      commonColumns.codigo,
      commonColumns.financeiro,
    ],
    ACN: [
      commonColumns.pessoa,
      commonColumns.ativo,
      commonColumns.ticker,
      commonColumns.isin,
      commonColumns.financeiro,
    ],
    DRI: [
      commonColumns.pessoa,
      commonColumns.ativo,
      commonColumns.vencimento,
      commonColumns.ticker,
      commonColumns.isin,
      commonColumns.codigo,
      commonColumns.financeiro,
    ],
    CRP: [
      commonColumns.pessoa,
      commonColumns.ativo,
      commonColumns.ticker,
      commonColumns.financeiro,
    ],
    IMV: [commonColumns.descricao, commonColumns.financeiro],
    BEM: [commonColumns.descricao, commonColumns.financeiro],
  };

  const handleChange = (_event, newValue) => {
    setValue(newValue);
  };

  const agruparPorAtivoTipoId = useCallback((pRegra) => {
    // Verifica se o input é um array
    if (!Array.isArray(pRegra)) {
      console.error("O parâmetro deve ser um array.");
      return {};
    }

    const agrupado = {};

    pRegra.forEach((item) => {
      const tipoId = item.ativo_tipo_id;

      // Se o tipo não existir no objeto, cria um array vazio para ele
      if (!agrupado[tipoId]) {
        agrupado[tipoId] = [];
      }

      // Adiciona o item ao grupo correspondente
      agrupado[tipoId].push(item);
    });

    return agrupado;
  }, []);

  useEffect(() => {
    if (!validators.isNull(regra.detalhe)) {
      const xGroupByAtivoTipo = agruparPorAtivoTipoId(regra.detalhe);
      const xFirstKey = Object.keys(xGroupByAtivoTipo)[0];
      setValue(xFirstKey);
      setGroupByAtivoTipo(xGroupByAtivoTipo);
    }
  }, [regra.detalhe, agruparPorAtivoTipoId]);

  return (
    <>
      <TabContext value={value}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <TabList
            onChange={handleChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            aria-label="Ativo Tipo"
          >
            {Object.entries(groupByAtivoTipo).map(([xKey, xValue]) => {
              return (
                <Tab
                  key={`tab-${xKey}`}
                  label={ATIVO_TIPO[xKey]?.sigla}
                  value={xKey}
                />
              );
            })}
          </TabList>
        </Box>
      </TabContext>

      <Stack pb={6}>
        <Stack py={1} px={2}>
          <Typography variant="caption" color="textSecondary">
            {ATIVO_TIPO[value]?.descricao}
          </Typography>
        </Stack>
        <Divider />
        <TableContainer
          id="table-container"
          sx={{
            overflow: "auto",
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                {value &&
                  columns[ATIVO_TIPO[value]?.mercado]?.map((column) => (
                    <TableCell key={column.key} align={column.align}>
                      {column.label}
                    </TableCell>
                  ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell
                  colSpan={
                    value && columns[ATIVO_TIPO[value]?.mercado].length - 1
                  }
                ></TableCell>
                <TableCell align="right">
                  <b>
                    {!validators.isEmpty(groupByAtivoTipo) &&
                      formats.formatNumber(
                        groupByAtivoTipo[value]?.reduce(
                          (acc, curr) => acc + curr.financeiro,
                          0
                        ) || 0,
                        2,
                        true
                      )}
                  </b>
                </TableCell>
              </TableRow>
              {!validators.isEmpty(groupByAtivoTipo) &&
                groupByAtivoTipo[value].map((xAtivo) => {
                  const xMercado = ATIVO_TIPO[value]?.mercado;
                  const xPath = xMercado
                    ? `/ativos${PATHS[xMercado.toLowerCase()].path}/${
                        xAtivo.ifeed_ativo_id
                      }`
                    : null;

                  return (
                    <TableRow key={xAtivo.isin}>
                      {value &&
                        columns[ATIVO_TIPO[value]?.mercado]?.map((column) => {
                          if (column.key === "pessoa") {
                            return (
                              <TableCell
                                key={column.key}
                                sx={{ width: "24px" }}
                              >
                                <Link
                                  to={`/empresas/${xAtivo.emissor_pessoa_id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <PessoaImage
                                    logoPessoaId={xAtivo.logo_pessoa_id}
                                    alt={xAtivo?.emissor?.pessoa_fantasia}
                                    hasLogo={Boolean(xAtivo?.logo_pessoa_id)}
                                    size={24}
                                  />
                                </Link>
                              </TableCell>
                            );
                          }

                          const value = xAtivo[column.key];

                          if (column.key === "ativo") {
                            return (
                              <TableCell
                                key={column.key}
                                sx={{
                                  width: "24px",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                <Badge
                                  badgeContent={xAtivo.marcacao}
                                  color="warning"
                                  anchorOrigin={{
                                    vertical: "top",
                                    horizontal: "right",
                                  }}
                                >
                                  <Box pr={1.5}>
                                    {xPath ? (
                                      <Typography
                                        component={Link}
                                        to={xPath}
                                        color="primary"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        variant="body2"
                                        aria-label={`Abrir ${value} em uma nova aba`}
                                      >
                                        {value}
                                      </Typography>
                                    ) : (
                                      value
                                    )}
                                  </Box>
                                </Badge>
                              </TableCell>
                            );
                          }

                          if (column.key === "emissor") {
                            return (
                              <TableCell
                                key={column.key}
                                sx={{
                                  width: "24px",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {value?.pessoa_fantasia}
                              </TableCell>
                            );
                          }

                          const isDate = [
                            "operacao_data",
                            "emissao",
                            "vencimento",
                          ].includes(column.key);

                          const isNumber = [
                            "quantidade",
                            "pu",
                            "financeiro",
                            "taxa",
                          ].includes(column.key);

                          return (
                            <TableCell key={column.key} align={column.align}>
                              {isNumber && value
                                ? formats.formatNumber(value, 2, true)
                                : isDate && value
                                ? formats.formatDateCustom(value, "DD/MM/YYYY")
                                : value}

                              {}
                            </TableCell>
                          );
                        })}
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
    </>
  );
});

export default ConcentracaoDetalhesAtivos;
